<template>
  <b-modal
    v-model="modalVisible"
    :title="isEdit ? 'تعديل سجل Geodesia' : 'إضافة سجل Geodesia'"
    size="lg"
    :ok-disabled="submitting || loadingRecord"
    ok-title="حفظ"
    cancel-title="إلغاء"
    no-close-on-backdrop
    @ok.prevent="onSubmit"
    @hidden="onHidden"
  >
    <div
      v-if="loadingRecord"
      class="text-center my-4"
    >
      <b-spinner class="mr-1" />
      <div class="text-muted">
        جاري تحميل البيانات...
      </div>
    </div>

    <b-form
      v-else
      @submit.prevent="onSubmit"
    >
      <b-row>
        
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label="كود الرفع المساحي *"
            label-for="survey_coding"
          >
            <b-form-input
              id="survey_coding"
              v-model.trim="form.survey_coding"
              :state="fieldState('survey_coding')"
              placeholder="مثال: SURV-123-S1"
            />
            <small
              v-if="errors.survey_coding"
              class="text-danger"
            >{{ errors.survey_coding }}</small>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group
            label="حالة Geodesia *"
            label-for="geodesia_status"
          >
            <v-select
              id="geodesia_status"
              v-model="form.geodesia_status"
              :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
              :options="statusOptions"
              :reduce="val => val.value"
              label="label"
              :placeholder="'اختر الحالة'"
              :state="fieldState('geodesia_status')"
            >
              <template #option="{ label }">
                <span>{{ label }}</span>
              </template>
            </v-select>
            <small
              v-if="errors.geodesia_status"
              class="text-danger"
            >{{ errors.geodesia_status }}</small>
          </b-form-group>

          <small class="text-muted d-block mt-1">
            سيستخدم التطبيق قيم الحقول: `survey_coding` و `geodesia_status`.
          </small>
        </b-col>
      </b-row>
    </b-form>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import geodesiaApi from '@/api/geodesia/geodesia'

export default {
  name: 'GeodesiaRecordForm',
  components: {
    BModal,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BSpinner,
    vSelect,
  },
  props: {
    value: { type: Boolean, default: false },
    recordId: { type: [Number, String, null], default: null },
  },
  data() {
    return {
      submitting: false,
      loadingRecord: false,
      form: {
        survey_coding: '',
        geodesia_status: 'مقبول',
      },
      errors: {},
      statusOptions: [
        { value: 'مقبول', label: 'مقبول' },
        { value: 'مرفوض', label: 'مرفوض' },
      ],
    }
  },
  computed: {
    isEdit() {
      return this.recordId !== null && this.recordId !== '' && this.recordId !== undefined
    },
    modalVisible: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },
  },
  watch: {
    value(newVal) {
      if (!newVal) return
      if (this.isEdit) this.loadRecord()
      else this.resetForm()
    },
    recordId() {
      if (this.value && this.isEdit) this.loadRecord()
    },
  },
  methods: {
    fieldState(field) {
      if (this.errors[field]) return false
      return null
    },
    resetForm() {
      this.loadingRecord = false
      this.submitting = false
      this.errors = {}
      this.form = {
        survey_coding: '',
        geodesia_status: 'مقبول',
      }
    },
    onHidden() {
      this.resetForm()
    },
    async loadRecord() {
      this.loadingRecord = true
      this.submitting = false
      this.errors = {}

      try {
        const response = await geodesiaApi.getGeodesiaRecord(this.recordId)
        const record = response && response.data ? response.data : (response || {})

        this.form.survey_coding = record.survey_coding != null
          ? record.survey_coding
          : (record.survey_code != null ? record.survey_code : '')

        this.form.geodesia_status = record.geodesia_status != null
          ? record.geodesia_status
          : (record.Geodesia_status != null ? record.Geodesia_status : 'مقبول')
      } catch (err) {
        const message = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'حدث خطأ أثناء تحميل البيانات'
        this.$toast.error(message)
        this.resetForm()
      } finally {
        this.loadingRecord = false
      }
    },
    validate() {
      const nextErrors = {}
      if (!this.form.survey_coding || !String(this.form.survey_coding).trim()) nextErrors.survey_coding = 'كود الرفع مطلوب'
      if (!this.form.geodesia_status || !String(this.form.geodesia_status).trim()) nextErrors.geodesia_status = 'الحالة مطلوبة'
      this.errors = nextErrors
      return Object.keys(nextErrors).length === 0
    },
    buildPayload() {
      const payload = {
        survey_coding: String(this.form.survey_coding).trim(),
        geodesia_status: String(this.form.geodesia_status).trim(),
      }

      // Backend compatibility notes: updates may accept aliases.
      if (this.isEdit) {
        payload.survey_code = payload.survey_coding
        payload.Geodesia_status = payload.geodesia_status
      }

      return payload
    },
    async onSubmit() {
      if (this.submitting || this.loadingRecord) return
      if (!this.validate()) {
        this.$toast.error('يرجى مراجعة بيانات النموذج')
        return
      }

      this.submitting = true
      try {
        const payload = this.buildPayload()
        const response = this.isEdit
          ? await geodesiaApi.updateGeodesiaRecord(this.recordId, payload)
          : await geodesiaApi.createGeodesiaRecord(payload)

        if (response && response.success) {
          this.$toast.success('تم الحفظ بنجاح')
          this.$emit('saved', response)
          this.modalVisible = false
        } else {
          const message = response && response.message ? response.message : 'فشل الحفظ'
          this.$toast.error(message)
        }
      } catch (err) {
        const message = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'حدث خطأ أثناء الحفظ'
        this.$toast.error(message)
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped></style>
